import {
  Box,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuList,
  Portal,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { IconVideos } from 'central-icons/IconVideos';
import { isToday, startOfTomorrow } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookie } from 'react-use';

import {
  useContactSalesMutation,
  useGetLoggedInUser,
  useUserTrialDetails,
} from '@revelio/auth';
import {
  CTAInteractionTrackingEvents,
  FeatureFlag,
  PagePaths,
  useTourStore,
} from '@revelio/core';
import { type KeyValueResp, MetadataKey } from '@revelio/data-access';
import {
  Badge,
  BadgeColor,
  Menu as UiMenu,
  MenuButton as UiMenuButton,
  MenuDivider as UiMenuDivider,
  MenuGroup as UiMenuGroup,
  MenuItem as UiMenuItem,
  MenuList as UiMenuList,
} from '@revelio/ui';

import { useUserMetadata } from '../../../../hooks/admin/useUserMetadata';
import { useBrand } from '../../../../shared/branding/brand-provider';
import {
  Tutorial,
  type VideoData,
  videoMetadata,
} from '../../../../shared/ui/tutorial/Tutorial';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import {
  toastContactSalesAlreadyNotified,
  toastContactSalesRequestError,
  toastContactSalesRequestSuccess,
} from '../../../trialUser/contact-sales-toast';
import { useFreeTrialAlertModalState } from '../../../trialUser/useFreeTrialAlertModalState';
import { iconUrls } from '../icons';
import navStyles from '../side-nav.module.css';
import { MenuItemWithIcon } from './menu-item-with-icon';
import menuStyles from './menus.module.css';
import {
  type MethodologyMenuItem,
  useMethodologyMenuItems,
} from './use-methodoloy-menu-items';

const tutorialHiddenPaths = [
  PagePaths.RESUME_ENRICHMENT,
  'sentiment-ratings',
  'sentiment-reviews',
  'admin',
];

export const MethodologyMenu = () => {
  const { showMenu, menuItems } = useMethodologyMenuItems();
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const location = useLocation();
  const [autoOpen, setAutoOpen] = useState(false);
  const [wasAutoOpened, setWasAutoOpened] = useState(false);

  const toast = useToast();
  const { loggedInUser, refetch: refetchUserData } = useGetLoggedInUser();
  const { updateMetadata } = useUserMetadata();
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const { email } = loggedInUser;
  const { days } = useFreeTrialAlertModalState();

  const { metadata } = loggedInUser;

  const [openedTutorials, updateOpenedTutorials] =
    useCookie('opened-tutorials');

  const isWhitelabel = useBrand().clientName !== 'default';

  const showTutorial =
    !isWhitelabel &&
    !tutorialHiddenPaths.some((path) => location?.pathname?.includes(path));

  const userWatchedIds = useMemo(() => {
    return new Set(
      JSON.parse(
        metadata?.find(
          (item: KeyValueResp | null) =>
            item?.key === MetadataKey.StepsCompleted
        )?.value || '[]'
      )
    );
  }, [metadata]);

  const mixpanelContactSales = () => {
    mixpanel.track(CTAInteractionTrackingEvents.CONTACT_SALES_CLICK, {
      days_left: days,
    });
  };

  const { mutate: contactSales } = useContactSalesMutation();

  const handleContactSales = () => {
    mixpanelContactSales();

    const contactedSales = localStorage.getItem('contacted-sales') || '';
    if (!contactedSales || !isToday(new Date(contactedSales))) {
      contactSales(
        { email, trialUpgrade: location.pathname },
        {
          onSuccess: () => {
            localStorage.setItem('contacted-sales', new Date().toDateString());
            toastContactSalesRequestSuccess(toast);
          },
          onError: () => {
            toastContactSalesRequestError(toast);
          },
        }
      );
    } else {
      toastContactSalesAlreadyNotified(toast);
    }
  };

  const handleTutorialSeen = async () => {
    try {
      if (!videoData?.id) return;
      if (userWatchedIds.has(videoData.id)) return;

      const updatedIds = JSON.stringify([
        ...Array.from(userWatchedIds),
        videoData.id,
      ]);

      await updateMetadata(loggedInUser, [
        {
          key: MetadataKey.StepsCompleted,
          value: updatedIds,
        },
      ]);

      // Ensure UI reflects latest metadata
      if (typeof refetchUserData === 'function') {
        refetchUserData();
      }
    } catch {
      // noop
    }
  };

  const getVideoDataFromPath = (pathname: string): VideoData | null => {
    for (const category of videoMetadata) {
      const video = category.videos.find((v) => v.paths.includes(pathname));
      if (video) {
        return { ...video };
      }
    }
    return null;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  const videoData = useMemo(() => {
    if (!showTutorial) return null;
    if (location.pathname.startsWith('/reports/')) {
      return getVideoDataFromPath('/reports/');
    }
    if (location.pathname.startsWith('/report-builder/')) {
      return getVideoDataFromPath('/report-builder/');
    }
    if (location.pathname.startsWith('/sample-report')) {
      return getVideoDataFromPath('/report-builder/');
    }
    return getVideoDataFromPath(location.pathname);
  }, [location.pathname, showTutorial]);

  const videoId = useMemo(() => videoData?.id, [videoData]);

  useEffect(() => {
    if (
      showTutorial &&
      videoId &&
      !userWatchedIds.has(videoId) &&
      isTrialUser
    ) {
      setAutoOpen(true);
    } else {
      setAutoOpen(false);
    }
  }, [showTutorial, videoId, userWatchedIds, isTrialUser]);

  const {
    isOpen: isMenuOpen,
    onOpen: onMenuOpen,
    onClose: onMenuClose,
  } = useDisclosure();
  const {
    isOpen: isTutorialOpen,
    onOpen: onTutorialOpen,
    onClose: onTutorialClose,
  } = useDisclosure();

  useEffect(() => {
    const openedTutorialsArray = JSON.parse(openedTutorials || '[]');
    if (
      showTutorial &&
      autoOpen &&
      videoId &&
      !openedTutorialsArray.includes(videoId)
    ) {
      onTutorialOpen();
      setWasAutoOpened(true);
      const nextDayStart = startOfTomorrow();
      updateOpenedTutorials(
        JSON.stringify([...openedTutorialsArray, videoId]),
        { expires: nextDayStart }
      );
    }
  }, [
    showTutorial,
    autoOpen,
    videoId,
    onTutorialOpen,
    updateOpenedTutorials,
    openedTutorials,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (
      showTutorial &&
      isTutorialOpen &&
      wasAutoOpened &&
      !['/deliverables/data-enrichment'].includes(location.pathname) &&
      autoOpen
    ) {
      const timer = setTimeout(() => {
        onTutorialClose();
      }, 6000);

      return () => clearTimeout(timer);
    }
    return () => null;
  }, [
    showTutorial,
    isTutorialOpen,
    onTutorialClose,
    location.pathname,
    wasAutoOpened,
  ]);

  /** Returns true when the tutorial hijacked the open (so click handlers can
   *  suppress the menu's own toggle). */
  const openMenuOrTutorial = () => {
    const shouldShowTutorial =
      isTrialUser &&
      showTutorial &&
      !!videoData &&
      !userWatchedIds.has(videoData.id);

    if (shouldShowTutorial) {
      onMenuClose();
      onTutorialOpen();
      return true;
    }
    onMenuOpen();
    return false;
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (openMenuOrTutorial()) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const setVideoId = useTourStore((s) => s.setVideoId);
  const setIsVideoOpen = useTourStore((s) => s.setIsVideoOpen);

  const handleWatchClick = () => {
    onTutorialClose();
    if (videoData?.id) {
      setVideoId(videoData.id);
    }
    setIsVideoOpen(true);
  };

  const handleThemedMenuOpenChange = (open: boolean) => {
    if (!open) {
      onMenuClose();
      return;
    }
    openMenuOrTutorial();
  };

  const renderUiItem = (item: MethodologyMenuItem) => {
    const Icon = item.icon;
    return (
      <UiMenuItem
        key={item.id}
        onClick={item.onClick}
        icon={
          Icon ? (
            <Icon size={16} />
          ) : (
            <img src={item.iconSrc} alt="" className={menuStyles.imageIcon} />
          )
        }
      >
        {item.children}
      </UiMenuItem>
    );
  };

  if (!showMenu) return null;

  const triggerIcon = (
    <>
      <Image
        className={navStyles.legacyRailIcon}
        w={5}
        h={5}
        src={iconUrls.methodology}
      />
      <Box
        as="span"
        className={navStyles.maskIcon}
        sx={{
          maskImage: `url("${iconUrls.methodologyThemed}")`,
          WebkitMaskImage: `url("${iconUrls.methodologyThemed}")`,
        }}
      />
    </>
  );

  const unwatchedBadge = showTutorial &&
    videoData &&
    !userWatchedIds.has(videoData?.id) && (
      <Box position="absolute" top="-2px" right="2px" zIndex="0">
        <Badge
          size="sm"
          color={BadgeColor.PRIMARY}
          className={menuStyles.unwatchedIndicator}
        />
      </Box>
    );

  return (
    <Tutorial
      isOpen={isTutorialOpen}
      onClose={() => {
        onTutorialClose();
        setAutoOpen(false);
      }}
      onBack={() => {
        onTutorialClose();
        onMenuOpen();
      }}
      videoData={videoData}
      onWatch={handleWatchClick}
      onContactSales={handleContactSales}
      onSeen={handleTutorialSeen}
      placement="right-end"
      gutter={4}
    >
      <Box display="inline-block">
        {isNewDashboardLayout ? (
          <UiMenu open={isMenuOpen} onOpenChange={handleThemedMenuOpenChange}>
            <Box position="relative" display="inline-block">
              <UiMenuButton>
                <IconButton
                  data-testid="methodology-menu"
                  aria-label="Methodology"
                  icon={triggerIcon}
                  variant="nav"
                  className="nav"
                />
              </UiMenuButton>
              {unwatchedBadge}
            </Box>
            <UiMenuList side="right" align="end">
              <UiMenuGroup label="Methodology">
                {menuItems.map(renderUiItem)}
              </UiMenuGroup>
              {showTutorial && videoData && (
                <>
                  <UiMenuDivider />
                  <UiMenuGroup label="Tutorials">
                    <UiMenuItem
                      onClick={() => onTutorialOpen()}
                      icon={
                        <IconVideos
                          size={16}
                          className={
                            userWatchedIds.has(videoData?.id)
                              ? undefined
                              : menuStyles.tutorialUnwatched
                          }
                        />
                      }
                    >
                      {videoData?.name} Tutorial
                    </UiMenuItem>
                  </UiMenuGroup>
                </>
              )}
            </UiMenuList>
          </UiMenu>
        ) : (
          <Menu
            placement="right-end"
            autoSelect={false}
            offset={[0, 4]}
            isOpen={isMenuOpen}
            onOpen={onMenuOpen}
            onClose={onMenuClose}
          >
            <Box position="relative" display="inline-block">
              <MenuButton
                data-testid="methodology-menu"
                as={IconButton}
                icon={triggerIcon}
                variant="nav"
                className="nav"
                onClick={handleMenuClick}
              />
              {unwatchedBadge}
            </Box>
            <Portal>
              <MenuList>
                <MenuGroup title="Methodology">
                  {menuItems.map((menuItemProps) => (
                    <MenuItemWithIcon
                      key={menuItemProps.id}
                      {...menuItemProps}
                    ></MenuItemWithIcon>
                  ))}
                </MenuGroup>
                {showTutorial && videoData && (
                  <>
                    <MenuDivider />
                    <MenuGroup title="Tutorials">
                      <MenuItemWithIcon
                        onClick={() => onTutorialOpen()}
                        icon={IconVideos}
                        iconColor={
                          userWatchedIds.has(videoData?.id)
                            ? undefined
                            : 'green.500'
                        }
                      >
                        {videoData?.name} Tutorial
                      </MenuItemWithIcon>
                    </MenuGroup>
                  </>
                )}
              </MenuList>
            </Portal>
          </Menu>
        )}
      </Box>
    </Tutorial>
  );
};
